import { useEffect, useRef, type RefObject } from 'react'

export const AVATAR_DURATION = 5.5
export const AVATAR_MIDPOINT = AVATAR_DURATION / 2

export function mapCursorToTime(clientX: number, viewportWidth: number, duration = AVATAR_DURATION) {
  if (viewportWidth <= 0) return duration / 2
  return Math.min(duration, Math.max(0, (clientX / viewportWidth) * duration))
}

interface AvatarScrubOptions {
  videoRef: RefObject<HTMLVideoElement | null>
  targetRef: RefObject<HTMLElement | null>
  enabled: boolean
}

export function useAvatarScrub({ videoRef, targetRef, enabled }: AvatarScrubOptions) {
  const targetTime = useRef(AVATAR_MIDPOINT)
  const displayedTime = useRef(AVATAR_MIDPOINT)
  const ready = useRef(false)

  useEffect(() => {
    if (!enabled) return
    const video = videoRef.current
    const target = targetRef.current
    if (!video || !target) return

    let frame = 0
    let disposed = false

    const setMiddle = () => {
      ready.current = true
      targetTime.current = AVATAR_MIDPOINT
      displayedTime.current = AVATAR_MIDPOINT
      video.currentTime = AVATAR_MIDPOINT
    }

    const onPointerMove = (event: PointerEvent) => {
      targetTime.current = mapCursorToTime(event.clientX, window.innerWidth)
    }

    const onPointerLeave = () => {
      targetTime.current = AVATAR_MIDPOINT
    }

    const tick = () => {
      if (disposed) return
      const delta = targetTime.current - displayedTime.current
      if (ready.current && !video.seeking && Math.abs(delta) > 0.018) {
        displayedTime.current = Math.min(AVATAR_DURATION, Math.max(0, displayedTime.current + delta * 0.24))
        video.currentTime = displayedTime.current
      }
      frame = window.requestAnimationFrame(tick)
    }

    video.addEventListener('loadedmetadata', setMiddle)
    target.addEventListener('pointermove', onPointerMove, { passive: true })
    target.addEventListener('pointerleave', onPointerLeave)
    if (video.readyState >= HTMLMediaElement.HAVE_METADATA) setMiddle()
    frame = window.requestAnimationFrame(tick)

    return () => {
      disposed = true
      window.cancelAnimationFrame(frame)
      video.removeEventListener('loadedmetadata', setMiddle)
      target.removeEventListener('pointermove', onPointerMove)
      target.removeEventListener('pointerleave', onPointerLeave)
    }
  }, [enabled, targetRef, videoRef])
}
